import JSZip from 'jszip';
import { degrees, PDFDocument, rgb } from 'pdf-lib';

export interface MeeshoPageCropBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MeeshoOrderInfo {
  index: number;
  fileName: string;
  pageIndex: number;
  customerName: string;
  customerCity: string;
  courier: string;
  destinationCode: string;
  sku: string;
  orderNo: string;
  invoiceNo: string;
  orderDate: string;
  quantity: number;
}

export interface PicklistEntry {
  sku: string;
  totalQty: number;
  orderCount: number;
  couriers: Record<string, number>;
}

export const DEFAULT_SHIPPING_CROP: MeeshoPageCropBounds = {
  x: 10,
  y: 492,
  width: 575,
  height: 340,
};

export const DEFAULT_INVOICE_CROP: MeeshoPageCropBounds = {
  x: 10,
  y: 148,
  width: 575,
  height: 344,
};

const A4_WIDTH = 595.28;
const A4_HEIGHT = 841.89;

export const SINGLE_ORDER_COMBINED_A4_LAYOUT = {
  width: A4_WIDTH,
  height: A4_HEIGHT,
  shippingCell: {
    x: 0,
    y: A4_HEIGHT / 2,
    width: A4_WIDTH / 2,
    height: A4_HEIGHT / 2,
  },
  invoiceCell: {
    x: 0,
    y: 0,
    width: A4_WIDTH,
    height: A4_HEIGHT / 2,
  },
} as const;

export type MeeshoLayoutOption =
  | '4in1'
  | 'thermal_landscape'
  | 'thermal_portrait'
  | 'single_cropped';

export type MeeshoInvoiceOption = 'separate_1in1' | 'separate_2in1' | 'skip';

export type MeeshoSortOption = 'default' | 'sku' | 'courier' | 'customer';

export async function countPdfPages(pdfBuffers: Uint8Array[]): Promise<number> {
  let pageCount = 0;

  for (const buffer of pdfBuffers) {
    const document = await PDFDocument.load(buffer);
    pageCount += document.getPageCount();
  }

  return pageCount;
}

/**
 * Extracts uncompressed text tokens from PDF buffer
 */
export function extractTextTokens(pdfBuffer: Uint8Array): string[] {
  try {
    const rawLatin = new TextDecoder('latin1').decode(pdfBuffer);
    const tokens: string[] = [];

    // Check for FlateDecode streams or uncompressed text
    const tjRegex = /\(([^)]+)\)Tj/g;
    let match: RegExpExecArray | null = tjRegex.exec(rawLatin);
    while (match !== null) {
      const val = match[1].replace(/\\([()\\])/g, '$1').trim();
      if (val) {
        tokens.push(val);
      }
      match = tjRegex.exec(rawLatin);
    }
    return tokens;
  } catch {
    return [];
  }
}

/**
 * Extracts metadata from text tokens or fallbacks
 */
export function parseMeeshoMetadataFromTokens(
  tokens: string[],
  pageIndex: number,
  fileName: string,
): MeeshoOrderInfo {
  let customerName = `Order #${pageIndex + 1}`;
  let customerCity = '';
  let courier = 'Standard';
  let destinationCode = '';
  let sku = 'Standard Item';
  let orderNo = '';
  let invoiceNo = '';
  let orderDate = '';
  let quantity = 1;

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t === 'Customer Address' && i + 1 < tokens.length) {
      customerName = tokens[i + 1];
      if (i + 5 < tokens.length) {
        customerCity = tokens[i + 5];
      }
    }
    if (
      t.includes('Delhivery') ||
      t.includes('Shadowfax') ||
      t.includes('Xpressbees') ||
      t.includes('Ecom Express') ||
      t.includes('Ekart') ||
      t.includes('Valmo')
    ) {
      courier = t;
    }
    if (t === 'Destination Code' && i + 1 < tokens.length) {
      destinationCode = tokens[i + 1];
    }
    if (t === 'SKU' && i + 5 < tokens.length) {
      sku = tokens[i + 5];
      const maybeQty = Number.parseInt(tokens[i + 7] || '1', 10);
      if (!Number.isNaN(maybeQty) && maybeQty > 0) {
        quantity = maybeQty;
      }
      if (tokens[i + 9]) {
        orderNo = tokens[i + 9];
      }
    }
    if (t === 'Invoice No.' && i + 1 < tokens.length) {
      invoiceNo = tokens[i + 1];
    }
    if (t === 'Order Date' && i + 1 < tokens.length) {
      orderDate = tokens[i + 1];
    }
  }

  return {
    index: pageIndex,
    fileName,
    pageIndex,
    customerName,
    customerCity,
    courier,
    destinationCode,
    sku,
    orderNo,
    invoiceNo,
    orderDate,
    quantity,
  };
}

/**
 * Creates single cropped PDF for shipping labels
 */
export async function cropShippingLabels(
  pdfBuffers: Uint8Array[],
  bounds = DEFAULT_SHIPPING_CROP,
): Promise<Uint8Array> {
  const outDoc = await PDFDocument.create();

  for (const buf of pdfBuffers) {
    const srcDoc = await PDFDocument.load(buf);
    for (const srcPage of srcDoc.getPages()) {
      const embedded = await outDoc.embedPage(
        srcPage,
        {
          left: bounds.x,
          bottom: bounds.y,
          right: bounds.x + bounds.width,
          top: bounds.y + bounds.height,
        },
        [1, 0, 0, 1, -bounds.x, -bounds.y],
      );
      const page = outDoc.addPage([bounds.width, bounds.height]);
      page.drawPage(embedded, {
        x: 0,
        y: 0,
        width: bounds.width,
        height: bounds.height,
      });
    }
  }

  return outDoc.save();
}

/**
 * Creates single cropped PDF for tax invoices
 */
export async function cropTaxInvoices(
  pdfBuffers: Uint8Array[],
  bounds = DEFAULT_INVOICE_CROP,
): Promise<Uint8Array> {
  const outDoc = await PDFDocument.create();

  for (const buf of pdfBuffers) {
    const srcDoc = await PDFDocument.load(buf);
    for (const srcPage of srcDoc.getPages()) {
      const embedded = await outDoc.embedPage(
        srcPage,
        {
          left: bounds.x,
          bottom: bounds.y,
          right: bounds.x + bounds.width,
          top: bounds.y + bounds.height,
        },
        [1, 0, 0, 1, -bounds.x, -bounds.y],
      );
      const page = outDoc.addPage([bounds.width, bounds.height]);
      page.drawPage(embedded, {
        x: 0,
        y: 0,
        width: bounds.width,
        height: bounds.height,
      });
    }
  }

  return outDoc.save();
}

/**
 * Generates 4-in-1 A4 layout: 4 shipping labels arranged in a 2x2 grid on standard A4 paper,
 * rotated 90 degrees by default to maximize label size on the page.
 */
export async function generate4in1A4(
  pdfBuffers: Uint8Array[],
  options: {
    includeCutMarks?: boolean;
    rotate90?: boolean;
    bounds?: MeeshoPageCropBounds;
  } = {},
): Promise<Uint8Array> {
  const { includeCutMarks = true, rotate90 = true, bounds = DEFAULT_SHIPPING_CROP } = options;

  const a4Doc = await PDFDocument.create();
  const a4Width = 595.28;
  const a4Height = 841.89;
  const cellWidth = a4Width / 2; // 297.64
  const cellHeight = a4Height / 2; // 420.94
  const margin = 14; // Safe 5mm printable margin for standard printers

  // Embed all source pages directly with crop bounding box and transformation matrix
  const embeddedLabels = [];
  for (const buf of pdfBuffers) {
    const srcDoc = await PDFDocument.load(buf);
    for (const srcPage of srcDoc.getPages()) {
      const embedded = await a4Doc.embedPage(
        srcPage,
        {
          left: bounds.x,
          bottom: bounds.y,
          right: bounds.x + bounds.width,
          top: bounds.y + bounds.height,
        },
        [1, 0, 0, 1, -bounds.x, -bounds.y],
      );
      embeddedLabels.push(embedded);
    }
  }

  const gridPositions = [
    { col: 0, row: 1 }, // Top-Left
    { col: 1, row: 1 }, // Top-Right
    { col: 0, row: 0 }, // Bottom-Left
    { col: 1, row: 0 }, // Bottom-Right
  ];

  for (let i = 0; i < embeddedLabels.length; i += 4) {
    const a4Page = a4Doc.addPage([a4Width, a4Height]);

    // Draw up to 4 labels on this page
    for (let slot = 0; slot < 4; slot++) {
      const labelIndex = i + slot;
      if (labelIndex >= embeddedLabels.length) break;

      const embedded = embeddedLabels[labelIndex];
      const pos = gridPositions[slot];
      const cellX = pos.col * cellWidth;
      const cellY = pos.row * cellHeight;

      if (rotate90) {
        // Rotate 90 degrees to fill the tall A4 quadrant
        const maxW = cellWidth - margin * 2;
        const maxH = cellHeight - margin * 2;
        const scale = Math.min(maxW / embedded.height, maxH / embedded.width);

        const fittedW = embedded.width * scale; // vertical size
        const fittedH = embedded.height * scale; // horizontal size

        const boxLeft = cellX + (cellWidth - fittedH) / 2;
        const boxBottom = cellY + (cellHeight - fittedW) / 2;

        a4Page.drawPage(embedded, {
          x: boxLeft + fittedH,
          y: boxBottom,
          xScale: scale,
          yScale: scale,
          rotate: degrees(90),
        });
      } else {
        const targetW = cellWidth - margin * 2;
        const targetH = cellHeight - margin * 2;
        const scale = Math.min(targetW / embedded.width, targetH / embedded.height);

        const fittedW = embedded.width * scale;
        const fittedH = embedded.height * scale;

        const drawX = cellX + (cellWidth - fittedW) / 2;
        const drawY = cellY + (cellHeight - fittedH) / 2;

        a4Page.drawPage(embedded, {
          x: drawX,
          y: drawY,
          xScale: scale,
          yScale: scale,
        });
      }
    }

    // Optional cut guidelines
    if (includeCutMarks) {
      // Vertical divider
      a4Page.drawLine({
        start: { x: a4Width / 2, y: 15 },
        end: { x: a4Width / 2, y: a4Height - 15 },
        thickness: 0.5,
        color: rgb(0.7, 0.7, 0.7),
        dashArray: [4, 4],
      });
      // Horizontal divider
      a4Page.drawLine({
        start: { x: 15, y: a4Height / 2 },
        end: { x: a4Width - 15, y: a4Height / 2 },
        thickness: 0.5,
        color: rgb(0.7, 0.7, 0.7),
        dashArray: [4, 4],
      });
    }
  }

  return a4Doc.save();
}

/**
 * Generates Thermal 4x6 layout (landscape 6x4 or portrait 4x6)
 */
export async function generateThermalLabels(
  pdfBuffers: Uint8Array[],
  orientation: 'landscape' | 'portrait' = 'landscape',
  bounds = DEFAULT_SHIPPING_CROP,
): Promise<Uint8Array> {
  const thermalDoc = await PDFDocument.create();

  const embeddedLabels = [];
  for (const buf of pdfBuffers) {
    const srcDoc = await PDFDocument.load(buf);
    for (const srcPage of srcDoc.getPages()) {
      const embedded = await thermalDoc.embedPage(
        srcPage,
        {
          left: bounds.x,
          bottom: bounds.y,
          right: bounds.x + bounds.width,
          top: bounds.y + bounds.height,
        },
        [1, 0, 0, 1, -bounds.x, -bounds.y],
      );
      embeddedLabels.push(embedded);
    }
  }

  // 4x6 inches: 288 x 432 pt (or 576 x 384 pt for crisp 6x4 thermal landscape)
  const pageWidth = orientation === 'landscape' ? 576 : 288;
  const pageHeight = orientation === 'landscape' ? 384 : 432;
  const margin = 8;

  for (let i = 0; i < embeddedLabels.length; i++) {
    const thermalPage = thermalDoc.addPage([pageWidth, pageHeight]);
    const embedded = embeddedLabels[i];

    if (orientation === 'portrait') {
      // Rotate 90 degrees to fill vertical 4x6 roll
      const maxW = pageWidth - margin * 2;
      const maxH = pageHeight - margin * 2;
      const scale = Math.min(maxW / embedded.height, maxH / embedded.width);

      const fittedW = embedded.width * scale;
      const fittedH = embedded.height * scale;

      const boxLeft = (pageWidth - fittedH) / 2;
      const boxBottom = (pageHeight - fittedW) / 2;

      thermalPage.drawPage(embedded, {
        x: boxLeft + fittedH,
        y: boxBottom,
        xScale: scale,
        yScale: scale,
        rotate: degrees(90),
      });
    } else {
      const targetW = pageWidth - margin * 2;
      const targetH = pageHeight - margin * 2;
      const scale = Math.min(targetW / embedded.width, targetH / embedded.height);

      const fittedW = embedded.width * scale;
      const fittedH = embedded.height * scale;

      const drawX = (pageWidth - fittedW) / 2;
      const drawY = (pageHeight - fittedH) / 2;

      thermalPage.drawPage(embedded, {
        x: drawX,
        y: drawY,
        xScale: scale,
        yScale: scale,
      });
    }
  }

  return thermalDoc.save();
}

/**
 * Generates 2-in-1 A4 layout for Tax Invoices (2 invoices per A4 page to save paper)
 */
export async function generate2in1Invoices(
  pdfBuffers: Uint8Array[],
  options: { includeCutMarks?: boolean; bounds?: MeeshoPageCropBounds } = {},
): Promise<Uint8Array> {
  const { includeCutMarks = true, bounds = DEFAULT_INVOICE_CROP } = options;

  const a4Doc = await PDFDocument.create();
  const a4Width = 595.28;
  const a4Height = 841.89;
  const cellHeight = a4Height / 2; // 420.94
  const margin = 16; // Safe printable margin for standard printers

  const embeddedInvoices = [];
  for (const buf of pdfBuffers) {
    const srcDoc = await PDFDocument.load(buf);
    for (const srcPage of srcDoc.getPages()) {
      const embedded = await a4Doc.embedPage(
        srcPage,
        {
          left: bounds.x,
          bottom: bounds.y,
          right: bounds.x + bounds.width,
          top: bounds.y + bounds.height,
        },
        [1, 0, 0, 1, -bounds.x, -bounds.y],
      );
      embeddedInvoices.push(embedded);
    }
  }

  for (let i = 0; i < embeddedInvoices.length; i += 2) {
    const a4Page = a4Doc.addPage([a4Width, a4Height]);

    // Draw up to 2 invoices (top and bottom)
    for (let slot = 0; slot < 2; slot++) {
      const invoiceIndex = i + slot;
      if (invoiceIndex >= embeddedInvoices.length) break;

      const embedded = embeddedInvoices[invoiceIndex];
      const cellY = slot === 0 ? cellHeight : 0; // slot 0 is top, slot 1 is bottom

      const targetW = a4Width - margin * 2;
      const targetH = cellHeight - margin * 2;
      const scale = Math.min(targetW / embedded.width, targetH / embedded.height);

      const fittedW = embedded.width * scale;
      const fittedH = embedded.height * scale;

      const drawX = (a4Width - fittedW) / 2;
      const drawY = cellY + (cellHeight - fittedH) / 2;

      a4Page.drawPage(embedded, {
        x: drawX,
        y: drawY,
        xScale: scale,
        yScale: scale,
      });
    }

    if (includeCutMarks) {
      a4Page.drawLine({
        start: { x: 15, y: a4Height / 2 },
        end: { x: a4Width - 15, y: a4Height / 2 },
        thickness: 0.5,
        color: rgb(0.7, 0.7, 0.7),
        dashArray: [4, 4],
      });
    }
  }

  return a4Doc.save();
}

/**
 * Places the shipping label and tax invoice from one source page on one A4 sheet.
 * This is used by the combined paper-saver output for a single order.
 */
export async function generateSingleOrderCombinedA4(
  pdfBuffers: Uint8Array[],
  options: {
    includeCutMarks?: boolean;
    rotateShipping90?: boolean;
    shippingBounds?: MeeshoPageCropBounds;
    invoiceBounds?: MeeshoPageCropBounds;
  } = {},
): Promise<Uint8Array> {
  const {
    includeCutMarks = true,
    rotateShipping90 = true,
    shippingBounds = DEFAULT_SHIPPING_CROP,
    invoiceBounds = DEFAULT_INVOICE_CROP,
  } = options;

  const sourceDocuments = await Promise.all(pdfBuffers.map((buffer) => PDFDocument.load(buffer)));
  const sourcePages = sourceDocuments.flatMap((document) => document.getPages());
  if (sourcePages.length !== 1) {
    throw new Error(
      `Single-order combined layout requires exactly one PDF page; received ${sourcePages.length}`,
    );
  }

  const outputDocument = await PDFDocument.create();
  const sourcePage = sourcePages[0];
  const shippingLabel = await outputDocument.embedPage(
    sourcePage,
    {
      left: shippingBounds.x,
      bottom: shippingBounds.y,
      right: shippingBounds.x + shippingBounds.width,
      top: shippingBounds.y + shippingBounds.height,
    },
    [1, 0, 0, 1, -shippingBounds.x, -shippingBounds.y],
  );
  const invoice = await outputDocument.embedPage(
    sourcePage,
    {
      left: invoiceBounds.x,
      bottom: invoiceBounds.y,
      right: invoiceBounds.x + invoiceBounds.width,
      top: invoiceBounds.y + invoiceBounds.height,
    },
    [1, 0, 0, 1, -invoiceBounds.x, -invoiceBounds.y],
  );

  const margin = 16;
  const {
    width: a4Width,
    height: a4Height,
    shippingCell,
    invoiceCell,
  } = SINGLE_ORDER_COMBINED_A4_LAYOUT;
  const page = outputDocument.addPage([a4Width, a4Height]);

  if (rotateShipping90) {
    const scale = Math.min(
      (shippingCell.width - margin * 2) / shippingLabel.height,
      (shippingCell.height - margin * 2) / shippingLabel.width,
    );
    const rotatedWidth = shippingLabel.height * scale;
    const rotatedHeight = shippingLabel.width * scale;
    const left = shippingCell.x + (shippingCell.width - rotatedWidth) / 2;
    const bottom = shippingCell.y + (shippingCell.height - rotatedHeight) / 2;
    page.drawPage(shippingLabel, {
      x: left + rotatedWidth,
      y: bottom,
      xScale: scale,
      yScale: scale,
      rotate: degrees(90),
    });
  } else {
    const scale = Math.min(
      (shippingCell.width - margin * 2) / shippingLabel.width,
      (shippingCell.height - margin * 2) / shippingLabel.height,
    );
    const width = shippingLabel.width * scale;
    const height = shippingLabel.height * scale;
    page.drawPage(shippingLabel, {
      x: shippingCell.x + (shippingCell.width - width) / 2,
      y: shippingCell.y + (shippingCell.height - height) / 2,
      xScale: scale,
      yScale: scale,
    });
  }

  const invoiceScale = Math.min(
    (invoiceCell.width - margin * 2) / invoice.width,
    (invoiceCell.height - margin * 2) / invoice.height,
  );
  const invoiceWidth = invoice.width * invoiceScale;
  const invoiceHeight = invoice.height * invoiceScale;
  page.drawPage(invoice, {
    x: invoiceCell.x + (invoiceCell.width - invoiceWidth) / 2,
    y: invoiceCell.y + (invoiceCell.height - invoiceHeight) / 2,
    xScale: invoiceScale,
    yScale: invoiceScale,
  });

  if (includeCutMarks) {
    page.drawLine({
      start: { x: 15, y: invoiceCell.height },
      end: { x: a4Width - 15, y: invoiceCell.height },
      thickness: 0.5,
      color: rgb(0.7, 0.7, 0.7),
      dashArray: [4, 4],
    });
    page.drawLine({
      start: { x: shippingCell.width, y: invoiceCell.height + 15 },
      end: { x: shippingCell.width, y: a4Height - 15 },
      thickness: 0.5,
      color: rgb(0.7, 0.7, 0.7),
      dashArray: [4, 4],
    });
  }

  return outputDocument.save();
}

/**
 * Merges multiple PDF buffers into a single combined PDF document
 */
export async function mergePdfs(pdfBuffers: Uint8Array[]): Promise<Uint8Array> {
  const mergedDoc = await PDFDocument.create();

  for (const buf of pdfBuffers) {
    if (!buf || buf.length === 0) continue;
    const doc = await PDFDocument.load(buf);
    const copiedPages = await mergedDoc.copyPages(doc, doc.getPageIndices());
    for (const page of copiedPages) {
      mergedDoc.addPage(page);
    }
  }

  return mergedDoc.save();
}

/**
 * Packages separated shipping labels and tax invoices into a ZIP archive
 */
export async function packageMeeshoZip(
  shippingPdf: Uint8Array,
  invoicesPdf?: Uint8Array,
): Promise<Uint8Array> {
  const zip = new JSZip();
  zip.file('meesho_shipping_labels.pdf', shippingPdf);
  if (invoicesPdf && invoicesPdf.length > 0) {
    zip.file('meesho_tax_invoices.pdf', invoicesPdf);
  }
  return zip.generateAsync({ type: 'uint8array' });
}

/**
 * Computes aggregated picklist summary by SKU
 */
export function calculatePicklist(orders: MeeshoOrderInfo[]): PicklistEntry[] {
  const map = new Map<string, PicklistEntry>();

  for (const o of orders) {
    const skuKey = o.sku || 'Unknown SKU';
    const entry = map.get(skuKey) || {
      sku: skuKey,
      totalQty: 0,
      orderCount: 0,
      couriers: {},
    };

    entry.totalQty += o.quantity || 1;
    entry.orderCount += 1;
    const courierKey = o.courier || 'Standard';
    entry.couriers[courierKey] = (entry.couriers[courierKey] || 0) + 1;

    map.set(skuKey, entry);
  }

  return Array.from(map.values()).sort((a, b) => b.totalQty - a.totalQty);
}

/**
 * Downloads a binary blob to the user's browser
 */
export function downloadBlob(
  data: Uint8Array | Blob,
  fileName: string,
  mimeType = 'application/pdf',
): void {
  const blob =
    data instanceof Blob ? data : new Blob([data as unknown as BlobPart], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
